/**
 * Mock data + a mock transport for offline / backend-down development.
 *
 * Enabled ONLY when `import.meta.env.VITE_USE_MOCKS === "true"` (see .env.example),
 * for offline UI work. There is no automatic fallback: a live build that loses the
 * backend surfaces the error rather than rendering these fixtures as real data.
 *
 * The shapes here are the CONTRACT the backend must match — they are the same
 * types in src/types/domain.ts and the {items,total,next_cursor} envelope.
 */
import type {
  Client,
  Device,
  HealthAlert,
  Me,
  SiteOverviewRow,
  SitesOverviewResponse,
} from "@/types/domain";
import type { Paginated } from "./types";

/** Wrap a plain array in the uniform list envelope. */
export function envelope<T>(items: T[]): Paginated<T> {
  return { items, total: items.length, next_cursor: null };
}

export const MOCK_ME: Me = {
  user: { id: "oidc|demo", email: "operator@example.net", display_name: "Demo Operator" },
  is_msp_admin: true,
  is_platform_admin: true,
  orgs: [
    { id: "org-1", name: "Acme Retail", slug: "acme" },
    { id: "org-2", name: "Northwind Hotels", slug: "northwind" },
    { id: "org-q", name: "Quarantine", slug: "quarantine", is_quarantine: true },
  ],
  sites: [
    { id: "site-1", org_id: "org-1", name: "SF Flagship", location: "San Francisco, CA" },
    { id: "site-2", org_id: "org-1", name: "Oakland DC", location: "Oakland, CA" },
    { id: "site-3", org_id: "org-2", name: "Portland Lobby", location: "Portland, OR" },
  ],
  roles: [
    { role: "msp_admin", org_id: "org-1", site_id: null },
    { role: "org_admin", org_id: "org-2", site_id: null },
  ],
};

// Device MACs are locally administered documentation values (02:00:00:...). The demo used to
// carry real lab hardware MACs, which then showed up in screenshots (UI review 2026-10-17).
export const MOCK_DEVICES: Device[] = [
  {
    serial: "OAP101-0001",
    org_id: "org-1",
    site_id: "site-1",
    name: "SF-Front-AP",
    model: "OAP101",
    mac: "02:00:00:00:00:11",
    firmware: "2.11.0",
    last_seen: new Date(Date.now() - 20_000).toISOString(),
    online: true,
    health: "healthy",
    ip: "10.111.100.11",
    clients_count: 24,
    uplink: { port: "eth0", speed_mbps: 2500, duplex: "full" },
    lldp: { system_name: "SF-IDF-6100", port_id: "1/1/7", mgmt_ip: "10.111.100.2", vlan: 100 },
  },
  {
    serial: "OAP101-0002",
    org_id: "org-1",
    site_id: "site-1",
    name: "SF-Warehouse-AP",
    model: "OAP101",
    mac: "02:00:00:00:00:12",
    firmware: "2.10.3",
    last_seen: new Date(Date.now() - 9 * 60_000).toISOString(),
    online: true,
    health: "warn",
    ip: "10.111.100.12",
    clients_count: 61,
    // Negotiated 1G on a 2.5G-capable uplink — a real "why is this AP slow" signal.
    uplink: { port: "eth0", speed_mbps: 1000, duplex: "full" },
    lldp: { system_name: "SF-IDF-6100", port_id: "1/1/12", mgmt_ip: "10.111.100.2", vlan: 100 },
  },
  {
    serial: "OAP101-0003",
    org_id: "org-1",
    site_id: "site-2",
    name: "OAK-Dock-AP",
    model: "OAP101",
    mac: "02:00:00:00:00:13",
    firmware: "2.10.3",
    last_seen: new Date(Date.now() - 46 * 60_000).toISOString(),
    online: false,
    health: "critical",
    ip: null,
    clients_count: 0,
    // Offline: link is down, so no negotiated speed; LLDP is the last-known neighbor.
    uplink: { port: "eth0" },
    lldp: { system_name: "OAK-MDF-2930F", port_id: "1/1/24", mgmt_ip: "10.111.101.2", vlan: 100 },
  },
  {
    serial: "ECS2100-10P-TIPC",
    org_id: "org-1",
    site_id: "site-1",
    name: "SF-IDF-Switch",
    model: "ECS2100-10P",
    mac: "02:00:00:00:00:21",
    firmware: "1.2.3.14",
    last_seen: new Date(Date.now() - 15_000).toISOString(),
    online: true,
    health: "healthy",
    ip: "172.20.0.106",
    // Switches report their own uplink to the core, plus a full port table below.
    uplink: { port: "1/1/10", speed_mbps: 1000, duplex: "full" },
    lldp: { system_name: "SF-Core-6300", port_id: "1/1/1", mgmt_ip: "172.20.0.1", vlan: 1 },
    ports: [
      { name: "1/1/1", status: "up", speed_mbps: 2500, duplex: "full", vlan: 100, poe: { enabled: true, powered_w: 6.4 }, lldp: { system_name: "SF-Front-AP", port_id: "eth0" } },
      { name: "1/1/7", status: "up", speed_mbps: 1000, duplex: "full", vlan: 100, poe: { enabled: true, powered_w: 5.9 }, lldp: { system_name: "SF-Warehouse-AP", port_id: "eth0" } },
      { name: "1/1/2", status: "up", speed_mbps: 1000, duplex: "full", vlan: 20, poe: { enabled: false }, lldp: null },
      { name: "1/1/3", status: "down", speed_mbps: null, vlan: 100, poe: { enabled: true, powered_w: 0 }, lldp: null },
      { name: "1/1/8", status: "disabled", vlan: 100, poe: { enabled: false }, lldp: null },
      { name: "1/1/10", status: "up", speed_mbps: 1000, duplex: "full", vlan: 1, poe: null, lldp: { system_name: "SF-Core-6300", port_id: "1/1/1", mgmt_ip: "172.20.0.1" } },
    ],
  },
];

export const MOCK_CLIENTS: Client[] = [
  {
    mac: "a4:83:e7:11:22:33",
    org_id: "org-1",
    site_id: "site-1",
    device_serial: "OAP101-0001",
    device_name: "SF-Front-AP",
    hostname: "iPad-Field-07",
    manufacturer: "Apple",
    ssid: "Acme-Corp",
    ip: "10.111.100.54",
    rssi: -58,
    band: "5GHz",
    vlan: 100,
    policy: "normal",
    connected_since: new Date(Date.now() - 42 * 60_000).toISOString(),
  },
  {
    mac: "3c:22:fb:aa:bb:cc",
    org_id: "org-1",
    site_id: "site-1",
    device_serial: "OAP101-0002",
    device_name: "SF-Warehouse-AP",
    hostname: "POS-Register-3",
    manufacturer: "Zebra",
    ssid: "Acme-POS",
    ip: "10.111.100.77",
    rssi: -71,
    band: "2.4GHz",
    vlan: 20,
    policy: "allowed",
    connected_since: new Date(Date.now() - 5 * 3600_000).toISOString(),
  },
];

export const MOCK_WORKLIST: HealthAlert[] = [
  {
    id: "al-1",
    org_id: "org-1",
    site_id: "site-2",
    severity: "critical",
    kind: "device_offline",
    title: "OAK-Dock-AP has been offline 46m",
    detail: "Last contact 46 minutes ago. 12 clients dropped.",
    target_type: "device",
    target_id: "OAP101-0003",
    created_at: new Date(Date.now() - 46 * 60_000).toISOString(),
    proposal_id: null,
  },
  {
    id: "al-2",
    org_id: "org-1",
    site_id: "site-1",
    severity: "warn",
    kind: "radio_saturation",
    title: "SF-Warehouse-AP 2.4GHz near saturation",
    detail: "61 clients on a single radio; channel utilization 88%.",
    target_type: "device",
    target_id: "OAP101-0002",
    created_at: new Date(Date.now() - 9 * 60_000).toISOString(),
    // A Phase-3 anomaly proposal exists — enables inline "Explain".
    proposal_id: "prop-88",
  },
];

/**
 * Customer Overview mock: the mock customer's real sites plus generated properties, so the
 * table can be exercised at the 100+ row scale it is designed for (search, sort, Show more).
 * Deterministic (no Math.random) so screenshots are repeatable.
 */
export function mockSitesOverview(orgId: string | null): SitesOverviewResponse {
  const org = MOCK_ME.orgs.find((o) => o.id === orgId) ?? MOCK_ME.orgs[0]!;
  const named = MOCK_ME.sites.filter((s) => s.org_id === org.id);
  const generated = Array.from({ length: 120 }, (_, i) => ({
    id: `gen-${i + 1}`,
    name: `Property ${String(i + 1).padStart(3, "0")}`,
    location: ["Calgary, AB", "Denver, CO", "Austin, TX", null][i % 4] ?? null,
  }));
  const items: SiteOverviewRow[] = [...named, ...generated].map((s, i) => {
    const total = s.id.startsWith("gen-") ? 3 + (i % 9) : 6;
    // A few sites carry offline devices / alerts / drift so every state renders.
    const offline = i % 17 === 3 ? total : i % 7 === 2 ? 1 + (i % 2) : 0;
    const online = total - offline;
    const alerts = offline > 0 ? 1 + (i % 2) : 0;
    const critical = offline === total ? 1 : 0;
    const differs = i % 11 === 5 ? 2 : 0;
    return {
      site_id: s.id,
      site_name: s.name,
      location: s.location ?? null,
      org_id: org.id,
      org_name: org.name,
      devices_total: total,
      devices_online: online,
      devices_offline: offline,
      clients_now: online * ((i % 5) + 2),
      alerts_open: alerts,
      alerts_critical: critical,
      online_pct: total ? Math.round((online * 100) / total) : null,
      health: critical ? "critical" : alerts || offline ? "warn" : "healthy",
      firmware: {
        status: differs ? "off_baseline" : i % 13 === 7 ? "no_baseline" : "current",
        matches: total - differs,
        differs,
        no_baseline: 0,
        unknown: 0,
      },
    };
  });
  const sum = (f: (r: SiteOverviewRow) => number) => items.reduce((n, r) => n + f(r), 0);
  return {
    generated_at: new Date().toISOString(),
    items,
    count: items.length,
    total: items.length,
    totals: {
      sites: items.length,
      customers: 1,
      devices: sum((r) => r.devices_total),
      offline: sum((r) => r.devices_offline),
      clients_now: sum((r) => r.clients_now),
      alerts_open: sum((r) => r.alerts_open),
    },
  };
}
